<script setup lang="ts">
import { useApiClient } from "@/api/client";
import { useRoute } from "vue-router";
import { ref } from "vue";
import dayjs from "dayjs";
import IntegrationExecutionDO = App.Data.Integrations.IntegrationExecutionDO;

const integrationUuid: string = String(useRoute().params.uuid) ?? "";
const isLoading = ref<boolean>(true);
const integrationExecutions = ref<IntegrationExecutionDO[]>([]);
import { debounce } from "vue-debounce";
import { formatNumber } from "chartjs-graph";
import useDialog from "@/ui/composables/useDialog";
import { toastApiResult } from "@/toaster";
import useToast from "@/ui/composables/useToast";

const itemsPerPage = ref(100);
const totalItemCount = ref(0);
const page = ref(1);
const sortBy = ref([]);

const ExecutionHeaders = [
	{ title: "Dispatch at", key: "dispatch", sortable: false },
	{ title: "Campaign", key: "utm_campaign", sortable: false },
	{ title: "Execution ID", key: "uuid", sortable: false },
	{ title: "Reservations", key: "size", sortable: false },
	{ title: "Batches", key: "batches", sortable: false },
	{ title: "Status", key: "status", sortable: false },
];

const BatchesHeaders = [
	{ title: "Synced to RTHA Data Platform", key: "updated_at", sortable: true },
	{ title: "Batch ID", key: "uuid", sortable: false },
	{ title: "Reservations", key: "size", sortable: false },
	{ title: "Status", key: "status", sortable: false },
];

const ColorStates = {
	pending: "warning",
	exported: "primary",
	failed: "error",
	cancelled: "error",
};

const getActivity = debounce(async ({ page, itemsPerPage, sortBy }) => {
	isLoading.value = true;

	const client = useApiClient("integrations");

	const response = await client.showActivity(
		{ page, itemsPerPage, sortBy },
		integrationUuid
	);

	if (response.isOk()) {
		integrationExecutions.value = response.value.data;
		totalItemCount.value = response.value.total;
	}

	isLoading.value = false;
}, 300);

const sumBatchSizes = (rtha_dp_batches) => {
	return rtha_dp_batches.reduce((total, batch) => {
		return total + (batch.size || 0);
	}, 0);
};

const isCancelAvailable = (dateTimeString: string): boolean => {
	return dayjs(dateTimeString).isAfter(dayjs());
};

const downloadExecutionContents = async (
	integrationExecution: IntegrationExecutionDO
) => {
	const response = await useApiClient("integrations").downloadExecutionContents(
		"rtha_dp",
		integrationExecution
	);

	if (response.isErr()) {
		useToast().error("Sorry something went wrong...", {
			timeout: 5000,
		});
	}
};

const cancelExecution = async (
	integrationExecution: IntegrationExecutionDO
) => {
	const confirm = await useDialog().confirm({
		title: "Cancel export to RTHA Data Platform",
		message:
			"Canceling an export to RTHA Data Platform can't be undone, are you sure you want to cancel?",
	});

	if (!confirm) {
		return;
	}

	const response = await toastApiResult(
		useApiClient("integrations").cancelExecution(
			"rtha_dp",
			integrationExecution
		)
	);

	if (response.isOk()) {
		getActivity({
			page: page.value,
			itemsPerPage: itemsPerPage.value,
			sortBy: sortBy.value,
		});
	}
};

const expanded = ref<string[]>([]);

function toggleRow(uuid: string) {
	if (expanded.value.includes(uuid)) {
		expanded.value = [];
	} else {
		expanded.value = [uuid];
	}
}
</script>

<template>
	<v-data-table-server
		v-model:items-per-page="itemsPerPage"
		v-model:page="page"
		v-model:sort-by="sortBy"
		v-model:expanded="expanded"
		:headers="ExecutionHeaders"
		:items="integrationExecutions"
		:items-length="totalItemCount"
		item-value="uuid"
		:loading="isLoading"
		@update:options="getActivity"
		:show-expand="true"
	>
		<template v-slot:item.data-table-expand="{ internalItem }">
			<v-btn
				:append-icon="
					expanded.includes(internalItem.raw.uuid)
						? 'mdi-chevron-up'
						: 'mdi-chevron-down'
				"
				:text="
					expanded.includes(internalItem.raw.uuid) ? 'Collapse' : 'More info'
				"
				class="text-none"
				color="medium-emphasis"
				size="small"
				variant="text"
				width="105"
				border
				slim
				@click="toggleRow(internalItem.raw.uuid)"
			/>
		</template>

		<template v-slot:item.dispatch="{ item }">
			<template v-if="item.rtha_dp_batches?.length > 0">
				{{
					dayjs(item.rtha_dp_batches[0].dispatch_offer_at).format(
						"dddd D MMM HH:mm"
					)
				}}
			</template>
			<span v-else>n/a</span>
		</template>
		<template v-slot:item.utm_campaign="{ item }">
			<template v-if="item.rtha_dp_batches?.length > 0">
				{{ item.rtha_dp_batches[0].utm_campaign }}
			</template>
			<span v-else>n/a</span>
		</template>
		<template v-slot:item.uuid="{ item }">
			..{{ item.uuid.slice(-7) }}
		</template>
		<template v-slot:item.status="{ item }">
			<v-chip label density="compact" :color="ColorStates[item.status]">{{
				item.status
			}}</v-chip>
		</template>
		<template v-slot:item.batches="{ item }">
			{{ item.rtha_dp_batches?.length ?? 0 }}
		</template>
		<template v-slot:item.size="{ item }">
			<template v-if="item.rtha_dp_batches?.length > 0">
				{{ sumBatchSizes(item.rtha_dp_batches) }}
			</template>
		</template>
		<template v-slot:expanded-row="{ columns, item }">
			<tr v-if="item.rtha_dp_batches?.length > 0">
				<td :colspan="columns.length" class="pa-5">
					<v-sheet color="grey-lighten-4" class="pa-3 rounded">
						<div class="grid-4-columns">
							<div>
								<div><strong>Campaign:</strong></div>
								<div>
									{{ item.rtha_dp_batches[0].utm_campaign }}
								</div>
							</div>

							<div>
								<div><strong>Source:</strong></div>
								<div v-if="item.exportable_type.endsWith('Segment')">
									<router-link
										class="router-link-primary"
										:to="{
											name: 'segments.configuration',
											params: {
												resource_type: 'segment',
												resource_uuid: item.exportable?.uuid,
											},
										}"
									>
										{{ item.exportable?.title }}
									</router-link>
								</div>
								<div v-else>Unknown</div>
							</div>

							<div>
								<div><strong>Upgrade Prospect:</strong></div>
								<div v-if="item.rtha_dp_batches[0].is_payment_required">
									Price:
									{{
										formatNumber(
											item.rtha_dp_batches[0].acceptance_fee,
											"money"
										)
									}}
								</div>
								<div v-else>Free Upgrade</div>
								<div>
									To product: {{ item.rtha_dp_batches[0].upgrade_product }}
								</div>
							</div>
						</div>
					</v-sheet>
					<v-divider />
					<div class="py-3">
						<i>{{ item.rtha_dp_batches[0].description }}</i>
					</div>
					<v-divider />
					<div class="d-flex w-100 align-center">
						<v-card-subtitle>Batches:</v-card-subtitle>
						<v-divider vertical class="mx-10" />
						<div class="w-100">
							<v-data-table
								:items="item.rtha_dp_batches"
								:headers="BatchesHeaders"
								density="compact"
							>
								<template v-slot:item.updated_at="{ item }">
									<template v-if="item.status === 'exported'">
										{{ dayjs(item.updated_at).format("dddd D MMM HH:mm") }}
									</template>
									<template v-else> - </template>
								</template>
								<template v-slot:item.uuid="{ item }">
									..{{ item.uuid.slice(-7) }}
								</template>
								<template v-slot:item.status="{ item }">
									<v-chip
										density="compact"
										label
										:color="ColorStates[item.status]"
										>{{ item.status }}</v-chip
									>
								</template>
								<template v-slot:bottom></template>
							</v-data-table>
						</div>
					</div>
					<v-divider />
					<div class="d-flex mt-5">
						<v-spacer />
						<v-btn
							prepend-icon="fa: fa-light fa-xmark"
							variant="text"
							density="compact"
							color="error"
							:disabled="
								!isCancelAvailable(item.rtha_dp_batches[0].dispatch_offer_at) ||
								item.status === 'cancelled'
							"
							@click="cancelExecution(item)"
						>
							Cancel
						</v-btn>
						<v-btn
							class="ml-5"
							prepend-icon="fa: fa-light fa-arrow-down-to-line"
							variant="flat"
							density="compact"
							color="primary"
							@click="downloadExecutionContents(item)"
						>
							Download Reservations
						</v-btn>
					</div>
				</td>
			</tr>
		</template>
	</v-data-table-server>
</template>

<style scoped lang="scss">
.grid-4-columns {
	display: grid;
	grid-template-columns: repeat(3, 1fr);
	gap: 1rem;
}
</style>
